import { Controller } from "stimulus";
const onmount = require("onmount");

export default class extends Controller {
  connect() {
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.element.addEventListener("click", this.onClick, true);
  }

  disconnect() {
    this.close();
    this.element.removeEventListener("click", this.onClick, true);
    this.onClick = null;
    this.onKeydown = null;
    this.onResize = null;
  }

  onClick(event) {
    const wrapper = event.target.closest(".bp-video-blob-wrapper");
    if (!wrapper) return;

    const link = wrapper.closest("a");
    if (!link) return;

    let videoUrl = link.dataset.videoUrl;
    if (!videoUrl) {
      const attachment = link.closest("action-text-attachment");
      if (attachment) videoUrl = attachment.getAttribute("url");
    }
    if (!videoUrl) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const poster = link.querySelector(".bp-video-poster, img");
    const posterSrc = poster ? poster.getAttribute("src") : null;

    const width = this.parseDimension(poster?.getAttribute("width")) || poster?.naturalWidth;
    const height = this.parseDimension(poster?.getAttribute("height")) || poster?.naturalHeight;

    this.open(videoUrl, posterSrc, {
      width,
      height,
      hlsUrl: link.dataset.hls,
      completeUrl: link.dataset.completeUrl
    });
  }

  parseDimension(value) {
    if (!value) return null;
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  onKeydown(event) {
    if (!this.overlay) return;
    if (event.key === "Escape") this.close();
  }

  open(videoUrl, posterSrc, options = {}) {
    this.close();

    this.videoUrl = videoUrl;

    const overlay = document.createElement("div");
    overlay.className = "bp-image-lightbox";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    const closeButton = this.buildButton("bp-image-lightbox-close", "Close", "&times;");

    const downloadButton = this.buildButton(
      "bp-image-lightbox-download",
      "Download",
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'
    );
    downloadButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.download();
    });

    const video = document.createElement("video");
    video.className = "bp-video-player";
    video.src = videoUrl;
    video.controls = true;
    video.playsInline = true;
    if (posterSrc) video.poster = posterSrc;
    if (options.hlsUrl) video.dataset.hls = options.hlsUrl;
    if (options.completeUrl) video.dataset.completeUrl = options.completeUrl;

    const playerWrapper = document.createElement("div");
    playerWrapper.className = "bp-image-lightbox-player";

    const { width, height } = options;
    this.naturalWidth = width || null;
    this.naturalHeight = height || null;
    this.playerWrapper = playerWrapper;
    this.fitPlayerToViewport();
    playerWrapper.appendChild(video);

    const stage = document.createElement("div");
    stage.className = "bp-image-lightbox-stage";
    stage.appendChild(closeButton);
    stage.appendChild(downloadButton);
    stage.appendChild(playerWrapper);

    overlay.appendChild(stage);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay || event.target === closeButton) {
        this.close();
      }
    });

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    this.overlay = overlay;
    this.videoElement = video;

    onmount();
    this.plyr = video.plyr || null;

    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);
  }

  onResize() {
    this.fitPlayerToViewport();
  }

  fitPlayerToViewport() {
    if (!this.playerWrapper) return;

    const horizontalPadding = window.innerWidth < 576 ? 24 : 48;
    const verticalPadding = window.innerWidth < 576 ? 96 : 80;
    const maxW = Math.max(0, window.innerWidth - horizontalPadding * 2);
    const maxH = Math.max(0, window.innerHeight - verticalPadding * 2);

    if (this.naturalWidth && this.naturalHeight) {
      const ratio = this.naturalWidth / this.naturalHeight;
      let w = maxW;
      let h = w / ratio;
      if (h > maxH) {
        h = maxH;
        w = h * ratio;
      }
      this.playerWrapper.style.width = `${w}px`;
      this.playerWrapper.style.height = `${h}px`;
    } else {
      this.playerWrapper.style.width = `${maxW}px`;
      this.playerWrapper.style.maxHeight = `${maxH}px`;
    }
  }

  buildButton(className, label, glyph) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.setAttribute("aria-label", label);
    button.innerHTML = glyph;
    return button;
  }

  download() {
    if (!this.videoUrl) return;
    const anchor = document.createElement("a");
    anchor.href = this.videoUrl;
    anchor.download = "";
    anchor.target = "_blank";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  close() {
    if (!this.overlay) return;

    if (this.plyr) {
      try { this.plyr.destroy(); } catch (_e) {}
    } else if (this.videoElement) {
      try { this.videoElement.pause(); } catch (_e) {}
      this.videoElement.removeAttribute("src");
      try { this.videoElement.load(); } catch (_e) {}
    }

    this.overlay.remove();
    document.body.style.overflow = "";
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown);
    if (this.onResize) window.removeEventListener("resize", this.onResize);

    this.overlay = null;
    this.videoElement = null;
    this.videoUrl = null;
    this.plyr = null;
    this.playerWrapper = null;
    this.naturalWidth = null;
    this.naturalHeight = null;
  }
}
